import { useState, useEffect } from "react";
import styles from "./banner.module.css";
import WhiteButton from "../../../../components/ui/ButtonModel/WhiteButton";
import { Breadcrumb } from "antd";
import downloadIcon from "../../../../assets/icons/Download.svg";
import ArrowRightIcon from "../../../../assets/icons/arrowTeamIcon.svg";
import BreadcrumbDropdown from "./BreadcrumbDropdown";
import { apiClient } from "../../../../services/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import { StarOutlined } from "@ant-design/icons";

export default function Banner({
  bannerYear,
  bannerDate,
  bannerEmployeeName,
  bannerTitle,
  showBreadcrumb = true,
  evaluationId,
  hasReport = false,
  showEvaluate = false,
  onEvaluate,
  isEvaluating = false,
  remainingDays,
  finalNote,
}: {
  bannerYear: string;
  bannerDate: string;
  bannerEmployeeName: string;
  bannerTitle: string;
  showBreadcrumb?: boolean;
  evaluationId?: string;
  hasReport?: boolean;
  showEvaluate?: boolean;
  onEvaluate?: () => void;
  isEvaluating?: boolean;
  remainingDays?: number;
  finalNote?: string;
}) {
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getLabelForPath = (path: string) => {
    const pathname = path.split("?")[0];

    if (pathname.startsWith("/report/")) return bannerYear;
    if (
      pathname.startsWith("/profile-team/") &&
      pathname.endsWith("/reports")
    ) {
      return "Relatórios";
    }
    if (pathname.startsWith("/profile-team/") && pathname.endsWith("/notes")) {
      return "Histórico de Notas";
    }
    if (pathname.startsWith("/profile-team/")) return bannerEmployeeName;
    if (pathname.startsWith("/employees/")) return "Colaborador";

    switch (pathname) {
      case "/home":
        return "Início";
      case "/my-team":
        return "A minha equipa";
      case "/all-employees":
        return "Colaboradores";
      case "/reports":
        return "Relatórios";
      case "/questionnaire":
        return "Questionário";
      case "/select-colleagues":
        return "Selecionar colegas";
      default:
        return "";
    }
  };

  const currentPath = `${location.pathname}${location.search}`;
  const storedHistory = (() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.sessionStorage.getItem("breadcrumbHistory");
      const history = raw ? JSON.parse(raw) : [];
      return Array.isArray(history) ? history : [];
    } catch {
      return [];
    }
  })();

  const historyWithCurrent = storedHistory.includes(currentPath)
    ? storedHistory
    : [...storedHistory, currentPath];

  const dedupedPaths = historyWithCurrent.reduce<string[]>((acc, path) => {
    if (acc.includes(path)) {
      return [...acc.filter((item) => item !== path), path];
    }
    return [...acc, path];
  }, []);

  const trailWithLabels = dedupedPaths.reduce<
    { path: string; label: string }[]
  >((acc, path) => {
    const label = getLabelForPath(path);
    if (!label) return acc;
    return [...acc, { path, label }];
  }, []);

  const trail = trailWithLabels.reduce<{ path: string; label: string }[]>(
    (acc, item) => {
      if (acc.find((existing) => existing.label === item.label)) {
        return [
          ...acc.filter((existing) => existing.label !== item.label),
          item,
        ];
      }
      return [...acc, item];
    },
    []
  );

  const toBreadcrumbItem = (
    item: { path: string; label: string },
    isCurrent: boolean
  ) => ({
    title: isCurrent ? (
      <b>{item.label}</b>
    ) : (
      <a
        onClick={(event) => {
          event.preventDefault();
          navigate(item.path);
        }}
      >
        {item.label}
      </a>
    ),
  });

  const breadcrumbItems =
    trail.length < 2
      ? []
      : isMobile
      ? (() => {
          const hiddenItems = trail.slice(0, -2);
          const tailItems = trail.slice(-2);
          const tailBreadcrumbItems = tailItems.map((item, index) =>
            toBreadcrumbItem(item, index === tailItems.length - 1)
          );

          if (!hiddenItems.length) return tailBreadcrumbItems;

          return [
            {
              title: (
                <BreadcrumbDropdown
                  trigger={<span className={styles.ellipsisButton}>...</span>}
                  items={hiddenItems.map((item) => ({
                    label: item.label,
                    href: item.path,
                  }))}
                />
              ),
            },
            ...tailBreadcrumbItems,
          ];
        })()
      : trail.map((item, index) =>
          toBreadcrumbItem(item, index === trail.length - 1)
        );

  const shouldShowBreadcrumb = showBreadcrumb && breadcrumbItems.length >= 2;

  return (
    <div className={styles.container}>
      <div className={styles.Banner}>
        <div className={styles.bannerContent}>
          <div>
            {shouldShowBreadcrumb && (
              <Breadcrumb
                className={styles.breadcrumb}
                separator={<img src={ArrowRightIcon} alt="arrow" />}
                items={breadcrumbItems}
              />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.bannerTitleContainer}>
              <div className={styles.bannerTitle}>{bannerTitle}</div>
              <div className={styles.bannerDate}>
                Data de submissão: {bannerDate}
              </div>
              {finalNote && (
                <div className={styles.finalNote}>
                  <span>Nota final:</span>
                  <span>{finalNote}</span>
                </div>
              )}
              {remainingDays !== undefined && (
                <div className={styles.remainingTime}>
                  Tempo restante: {remainingDays}{" "}
                  {remainingDays === 1 ? "dia" : "dias"}
                </div>
              )}
            </div>
            <div className={styles.bannerButtons}>
              {showEvaluate && (
                <WhiteButton
                  text={isEvaluating ? "A AVALIAR..." : "AVALIAR"}
                  className={styles.whiteButton}
                  icon={<StarOutlined aria-hidden="true" />}
                  onClick={onEvaluate}
                  disabled={isEvaluating}
                />
              )}
              {evaluationId && hasReport && (
                <WhiteButton
                  text="DOWNLOAD"
                  className={styles.whiteButton}
                  icon={downloadIcon}
                  invertIconOnHover={false}
                  onClick={async () => {
                    try {
                      const response = await apiClient.get(
                        `/Generate/${evaluationId}/pdf`,
                        {
                          responseType: "blob",
                        }
                      );

                      const blob = new Blob([response.data], {
                        type: "application/pdf",
                      });
                      const downloadUrl = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = downloadUrl;
                      link.download = `Relatorio_${bannerYear}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(downloadUrl);
                    } catch (error) {
                      console.error(
                        "Erro ao fazer download do relatório:",
                        error
                      );
                      alert("Erro ao fazer download do relatório.");
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
